import { useSyncExternalStore } from 'react';
/**
 * Connection awareness — Batch 1b.2 (11 Jun 2026), Batch 3.1 (12 Jun
 * 2026: iOS visibilitychange resync).
 *
 * Reports whether the outbox should attempt sends RIGHT NOW. Composes
 * three signals into one boolean:
 *
 *   1. `navigator.onLine` — the OS-level signal. Coarse but reliable
 *      on iOS / Android / desktop browsers when the device transitions
 *      between airplane-mode / wifi-toggled / etc.
 *
 *   2. window 'online' / 'offline' events — fire when navigator.onLine
 *      transitions. The module listens once at load and updates local
 *      state on each transition. SSR-safe — the listener attachment is
 *      guarded on `typeof window !== 'undefined'`.
 *
 *   3. Actual-API-call success / failure heartbeat — the ground truth.
 *      On caravan-park wifi the device is often connected to a wifi
 *      router whose upstream link is down; `navigator.onLine` stays
 *      `true` but every API call fails. The heartbeat catches this:
 *      a reported failure flips `shouldSend` to `false` until either
 *      the next reported success OR an offline→online transition
 *      resets the slate.
 *
 *   4. document 'visibilitychange' resync (Batch 3.1, 12 Jun 2026) —
 *      iOS Safari pauses JS execution when a tab is backgrounded.
 *      Window 'online' / 'offline' events that fire during the pause
 *      may be coalesced or dropped entirely, leaving state stale when
 *      the user returns to the tab. visibilitychange fires reliably
 *      when the tab becomes visible again; at that point we re-read
 *      `navigator.onLine` (which IS up-to-date on resume) and
 *      reconcile state. Critical for the primary use case — caravan
 *      guest leaves Safari to toggle wifi in Settings, returns to a
 *      stale "online" state with no banner.
 *
 * Composition:
 *
 *   shouldSend = isOnline && lastApiResult !== 'failure'
 *
 * Why the offline→online reset is necessary: without it, a single
 * past failure would gate the connection forever, even after the
 * device reconnects to a different network. Resetting on the
 * transition gives a recovered connection a fresh chance to prove
 * itself — the next API call's outcome restores the heartbeat
 * truthfully.
 *
 * Spurious 'online' events (some browsers fire them even when
 * `navigator.onLine` was already true) are filtered: the reset only
 * runs on a genuine offline→online transition, not on every fire.
 * Otherwise a flapping 'online' event would repeatedly clear a
 * legitimate 'failure' heartbeat.
 *
 * Module-level state with a pub/sub list — no Context, no Zustand,
 * no TanStack Query. The heartbeat is reported globally (any API
 * call site flips state via `reportApiSuccess` / `reportApiFailure`)
 * and the React hook subscribes per-mount via `useSyncExternalStore`.
 * That's the canonical React 18 pattern for external stores —
 * handles tearing across concurrent renders, no extra deps.
 *
 * Boundary on policy vs mechanism: this module is mechanism only.
 * It exposes `shouldSend` as advisory; callers decide whether to
 * gate on it. The Batch 1d UI wiring will read `useConnection()` in
 * MePage and decide policy (e.g. drain only on 'online' event, skip
 * proactive sends when shouldSend is false, render an offline
 * banner). Keeping policy out of this module keeps it pure and
 * easy to test.
 */
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
/**
 * Heartbeat state — outcome of the most recent API call we've been
 * told about. `null` = no recent activity (initial state, or just
 * after an offline→online transition).
 */
export type ApiResult = 'success' | 'failure' | null;
/**
 * Connection snapshot. `isOnline` and `lastApiResult` are the inputs;
 * `shouldSend` is derived. All three are surfaced so callers can
 * distinguish "device is offline" UX from "device is online but
 * recent API calls are failing" UX (e.g. different banner copy).
 */
export interface ConnectionState {
  isOnline: boolean;
  lastApiResult: ApiResult;
  shouldSend: boolean;
}
type Listener = (state: ConnectionState) => void;
// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------
// SSR guard — Vite Worker builds and any future SSG path import this
// module at build time when `window` doesn't exist. Without the
// guard, the listener attachment below would crash the build.
const SSR = typeof window === 'undefined';
function computeState(
  isOnline: boolean,
  lastApiResult: ApiResult,
): ConnectionState {
  return {
    isOnline,
    lastApiResult,
    shouldSend: isOnline && lastApiResult !== 'failure',
  };
}
// Defaults for SSR: assume online, no API history, can send. The
// shouldSend value is harmless in SSR because there's nothing to
// drain server-side; the in-browser hydration replaces it with the
// real navigator.onLine read on first client render.
const SSR_DEFAULT: ConnectionState = {
  isOnline: true,
  lastApiResult: null,
  shouldSend: true,
};
let state: ConnectionState = SSR
  ? SSR_DEFAULT
  : computeState(window.navigator.onLine, null);
const listeners = new Set<Listener>();
function setState(next: ConnectionState): void {
  // Notify only on actual change — avoids spurious re-renders in any
  // React subscriber and keeps the pub/sub well-behaved under flap
  // patterns (e.g. browser firing spurious 'online' events).
  if (
    next.isOnline === state.isOnline &&
    next.lastApiResult === state.lastApiResult
  ) {
    return;
  }
  state = next;
  for (const listener of listeners) {
    listener(state);
  }
}
// ---------------------------------------------------------------------------
// Window / document event wiring (one-time, on module load)
// ---------------------------------------------------------------------------
//
// 'online' fires when navigator.onLine flips true → reset lastApiResult
// to null on a GENUINE transition (state was offline). Treat spurious
// 'online' events while already online as no-ops so they don't
// repeatedly clear a legitimate 'failure' heartbeat.
//
// 'offline' fires when navigator.onLine flips false → preserve
// lastApiResult so the recovery flow has continuity, but shouldSend
// falls to false via the isOnline=false predicate.
//
// 'visibilitychange' (Batch 3.1, 12 Jun 2026) is the iOS Safari
// resync — see the module JSDoc above. Only acts on the
// hidden→visible transition; the visible→hidden transition doesn't
// need handling because state mutations while backgrounded are
// either reflected via the in-process pub/sub (reportApi* calls
// from any in-flight fetch resolve) or via this listener on the
// next foregrounding.
if (!SSR) {
  window.addEventListener('online', () => {
    if (state.isOnline) return; // spurious 'online' while already online
    setState(computeState(true, null));
  });
  window.addEventListener('offline', () => {
    setState(computeState(false, state.lastApiResult));
  });
  document.addEventListener('visibilitychange', () => {
    // Only act on the hidden→visible transition. The
    // visible→hidden transition is a no-op for our purposes: events
    // we miss while backgrounded are detected on the NEXT
    // foregrounding, not when going to background.
    if (document.visibilityState !== 'visible') return;

    const osIsOnline = window.navigator.onLine;
    if (osIsOnline === state.isOnline) {
      // Already in sync — either no transition happened while we
      // were backgrounded, OR the 'online' / 'offline' event handler
      // already caught it on resume. Either way, nothing to do.
      // The setState change-detection guard would no-op anyway, but
      // we short-circuit to avoid even constructing the next state
      // object.
      return;
    }

    // Mismatch detected — the OS state changed while we were
    // backgrounded and our window-event listeners missed it.
    // Reconcile by mirroring the corresponding event handler's
    // semantics EXACTLY, so a transition observed via
    // visibilitychange produces the same state as one observed via
    // the 'online' / 'offline' event in the foreground.
    if (osIsOnline) {
      // Missed offline→online: reset lastApiResult to null, giving
      // the recovered connection a fresh chance to prove itself.
      // Matches the 'online' event handler above.
      setState(computeState(true, null));
    } else {
      // Missed online→offline: preserve lastApiResult so the
      // recovery flow has continuity. Matches the 'offline' event
      // handler above.
      setState(computeState(false, state.lastApiResult));
    }
  });
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Snapshot the current connection state. Cheap — returns the module's
 * live state object. Safe to call from any context (render, effect,
 * event handler, module-load).
 *
 * The returned object is NOT frozen; callers must not mutate it.
 * Mutation would corrupt the next change-detection comparison in
 * `setState` and silently break the pub/sub.
 */
export function getConnectionState(): ConnectionState {
  return state;
}
/**
 * Subscribe to state changes. Returns an unsubscribe function.
 *
 * The listener fires AFTER state is updated, with the new state as
 * its argument. Listeners that don't need the value (e.g.
 * `useSyncExternalStore` adapters) can ignore the argument and call
 * `getConnectionState()` instead.
 *
 * Listeners are NOT notified on subscribe — call `getConnectionState`
 * for the initial value. This mirrors `useSyncExternalStore`'s
 * contract (the hook handles initial reads itself).
 */
export function subscribeConnection(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
/**
 * Report a successful API call. Flips `lastApiResult` to 'success'
 * and notifies subscribers (no-op if already 'success').
 *
 * Called from the SSE / fetch wrapper on every 2xx response. Cheap
 * — does NOT need to be debounced. Repeated calls with identical
 * state are filtered by the change-detection check in `setState`.
 *
 * Does NOT flip `isOnline` — the OS signal is the source of truth
 * for that. A successful API call while navigator.onLine reports
 * false (extremely rare but possible during a transition) doesn't
 * lie about connectivity, just records the heartbeat.
 */
export function reportApiSuccess(): void {
  setState(computeState(state.isOnline, 'success'));
}
/**
 * Report a failed API call. Flips `lastApiResult` to 'failure' and
 * notifies subscribers. `shouldSend` falls to `false` (assuming
 * isOnline is still true).
 *
 * Called from the SSE / fetch wrapper on network errors and 5xx
 * responses. NOT called on 4xx — those are application-level
 * problems (bad request, unauthorized, stay_ended), not connection
 * problems, and shouldn't gate the queue.
 *
 * Recovery paths:
 *   - Caller-driven: a subsequent `reportApiSuccess()` clears
 *     'failure' immediately. This is the common case — retry
 *     succeeded.
 *   - System-driven: an 'offline'→'online' transition resets
 *     `lastApiResult` to null. Catches the case where the device
 *     genuinely changed networks and we want a fresh slate.
 */
export function reportApiFailure(): void {
  setState(computeState(state.isOnline, 'failure'));
}
/**
 * React hook returning the live connection state. Re-renders the
 * caller on every meaningful state change.
 *
 * Uses `useSyncExternalStore` (React 18+) — the canonical pattern
 * for subscribing components to an external store. Handles tearing
 * during concurrent renders correctly, unlike a naïve
 * `useState + useEffect` pair.
 *
 * The SSR snapshot path returns `SSR_DEFAULT` (online + no history
 * + shouldSend true). Harmless because there's no outbox to drain
 * server-side; client hydration replaces it with real state on
 * first render.
 */
export function useConnection(): ConnectionState {
  return useSyncExternalStore<ConnectionState>(
    // Adapter — useSyncExternalStore wants `(onChange) => unsub`,
    // we have `(listener: (state) => void) => unsub`. Drop the
    // argument and call onChange to trigger re-read via getSnapshot.
    (onChange) => subscribeConnection(() => onChange()),
    getConnectionState,
    // Server snapshot — used during SSR / before hydration.
    () => SSR_DEFAULT,
  );
}
// ---------------------------------------------------------------------------
// Testing helper
// ---------------------------------------------------------------------------
/**
 * Reset module state and clear all subscribers. INTERNAL — for use
 * by unit tests only, not by production code. The underscore prefix
 * marks it as not part of the supported API surface.
 *
 * Without this, tests share the singleton state and pollute each
 * other (a 'failure' heartbeat set in test A leaks into test B's
 * initial state; a subscriber attached in test C still fires for
 * test D's state changes).
 *
 * Re-reads `navigator.onLine` so tests that mock it via vitest's
 * `vi.spyOn(navigator, 'onLine', 'get')` get the mocked value as
 * the reset baseline.
 *
 * Does NOT detach the window / document event listeners — those are
 * registered once at module load and persist across tests. That's
 * fine for test isolation because the listeners read `state` and
 * `navigator.onLine` fresh on every fire, and tests control both via
 * the reset + spy. Detaching would require holding handler
 * references at module scope just for tests, which isn't worth the
 * complication.
 */
export function _resetConnectionForTests(): void {
  listeners.clear();
  state = SSR
    ? SSR_DEFAULT
    : computeState(window.navigator.onLine, null);
}
